import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import useWebPush, { isIosDevice, canUseWebPush } from "../../hooks/useWebPush";

const DISMISS_KEY = "heidi_push_banner_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const useStyles = makeStyles(() => ({
  root: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1400,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "8px 12px",
    paddingTop: "calc(8px + env(safe-area-inset-top, 0px))",
    background: "#1e3a8a",
    color: "#fff",
    fontSize: 13,
    fontFamily: "Inter, system-ui, sans-serif",
    boxShadow: "0 2px 8px rgba(0,0,0,0.25)"
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0
  },
  button: {
    textTransform: "none",
    background: "#fff",
    color: "#1e3a8a",
    fontWeight: 600,
    fontSize: 12,
    padding: "4px 10px",
    minWidth: 0,
    "&:hover": {
      background: "#e2e8f0"
    }
  },
  close: {
    color: "#fff",
    padding: 4
  }
}));

function isDismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch (_) {
    return false;
  }
}

const PushNotificationBanner = () => {
  const classes = useStyles();
  const {
    supported,
    permission,
    subscribed,
    loading,
    isIos,
    isStandalone,
    subscribe,
    sendTest
  } = useWebPush();
  const [dismissed, setDismissed] = useState(() => isDismissedRecently());
  const [statusMsg, setStatusMsg] = useState("");

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (_) {
      /* ignore */
    }
    setDismissed(true);
  };

  if (dismissed) return null;
  if (!canUseWebPush() && !isIosDevice()) return null;

  if (isIos && !isStandalone) {
    return (
      <div className={classes.root}>
        <span>
          Para receber alertas no iPhone, adicione o HEIDI à Tela de Início
          (Compartilhar → Adicionar à Tela de Início) e abra pelo ícone.
        </span>
        <div className={classes.actions}>
          <IconButton
            className={classes.close}
            size="small"
            aria-label="Fechar"
            onClick={dismiss}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
    );
  }

  if (!supported) return null;
  if (permission === "denied") {
    return (
      <div className={classes.root} style={{ background: "#7f1d1d" }}>
        <span>
          Notificações bloqueadas. Ative em Ajustes → HEIDI → Notificações.
        </span>
        <div className={classes.actions}>
          <IconButton
            className={classes.close}
            size="small"
            onClick={dismiss}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
    );
  }

  if (subscribed && permission === "granted") {
    return null;
  }

  const handleEnable = async () => {
    try {
      await subscribe();
      setStatusMsg("Ativado");
      const result = await sendTest();
      if (result?.sent === 0) {
        setStatusMsg("Ativado — feche o app e teste de novo");
      }
    } catch (err) {
      setStatusMsg(err?.message || "Falha ao ativar");
    }
  };

  return (
    <div className={classes.root}>
      <span>
        {statusMsg ||
          "Ative notificações push para alertas com o app fechado."}
      </span>
      <div className={classes.actions}>
        <Button
          className={classes.button}
          disabled={loading}
          onClick={handleEnable}
        >
          {loading ? "…" : "Ativar"}
        </Button>
        <IconButton className={classes.close} size="small" onClick={dismiss}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
    </div>
  );
};

export default PushNotificationBanner;
